/** Distinct from `WRANGLER_ENV_KEYS` (the wrangler section name) and `ENVIRONMENTS` (the deployment label); PROD -> PRODUCTION is mapped at the wrangler-cmd boundary. */

import { ENVIRONMENTS, WRANGLER_ENV_KEYS } from '@federa/const/config-environments';
import type { DeployEnv } from '@federa/types/wrangler-deploy-env';

export const DEPLOY_ENV_TARGETS = {
	DEV: 'dev',
	PROD: 'prod',
} as const satisfies Record<string, DeployEnv>;

/**
 * @runtime-derived-ssot-helper -- the STRICT normalizer onto `DeployEnv`, returning `null` for a label this codebase's
 * env vocabularies do not spell. It owns the vocabulary; {@link toDeployEnv} is the same recognition under a
 * dev-defaulting policy.
 *
 * THE TWO POLICIES ARE NOT INTERCHANGEABLE, which is why both exist. A caller CHOOSING a resource may default an
 * absent label to dev -- the safe direction. A caller deciding whether it MAY TOUCH the environment a request named
 * must not: reading an absent label as dev is the fail-OPEN half of the silent-wrong-target class (a worker that
 * cannot say which environment it serves would answer a `-prod` request from its dev binding). Such a caller takes
 * this function and refuses on `null`.
 *
 * Distinct from `isDevFleetTarget` (`const/config-dev-fleet-databases.ts`), which answers the DESTRUCTIVE-ACTION
 * question -- "is this (env, database) pair provably a dev-fleet target" -- by requiring manifest membership of the
 * database NAME as well as this vocabulary. That predicate cannot express a MISMATCH between a requested target and a
 * serving one, so it is the wrong instrument for the comparison this function serves. It CONSUMES this one.
 */
export function toDeployEnvStrict(raw: string | undefined): DeployEnv | null {
	const lower = (raw ?? '').toLowerCase();
	if (lower === WRANGLER_ENV_KEYS.PRODUCTION || lower === DEPLOY_ENV_TARGETS.PROD) return DEPLOY_ENV_TARGETS.PROD;
	if (lower === ENVIRONMENTS.DEVELOPMENT || lower === DEPLOY_ENV_TARGETS.DEV) return DEPLOY_ENV_TARGETS.DEV;
	return null;
}

/**
 * Placeholder interpolated into a refusal diagnostic wherever the deployment env label is ABSENT, so the message never
 * renders `undefined`. It is the null arm of the vocabulary {@link toDeployEnvStrict} owns -- that function answers
 * `null` for "this input names no environment I recognise", and this is how that answer PRINTS -- which is why it lives
 * beside it rather than in the migration-tracker leaf.
 *
 * NOT scoped to any one family: read by the migration refusal copy (`utils/migrations/tracker-refusal-messages.ts`,
 * `utils/migrations/prod-target-refusal.ts`) and by the money-phase dev-only admission
 * (`const/money-phase-dev-only-guard.ts`), which refuses an unlabelled runtime on the same terms. One spelling of "this
 * isolate/target named no environment" across every refusal that has to print it.
 */
export const UNSET_ENV_DISPLAY = '<unset>';

/** @runtime-derived-ssot-helper -- the single normalizer from any env-name vocabulary (`WRANGLER_ENV_KEYS` / `ENVIRONMENTS` / raw binding `env` labels, any case) onto the short `DeployEnv` form. Unknown or empty input reads as dev, mirroring every resolver's dev default; the vocabulary itself lives in {@link toDeployEnvStrict} so the two policies cannot drift to two spellings. */
export function toDeployEnv(raw: string | undefined): DeployEnv {
	return toDeployEnvStrict(raw) ?? DEPLOY_ENV_TARGETS.DEV;
}

export const DEPLOY_ENV_TARGET_VALUES = [DEPLOY_ENV_TARGETS.DEV, DEPLOY_ENV_TARGETS.PROD] as const;

/**
 * @runtime-derived-ssot-helper -- every per-environment value of a `{ dev, prod }`-keyed table (worker deploy names,
 * AI Search instance ids), dev before prod per entry: the `Object.values(table).flatMap((pair) => [pair.dev,
 * pair.prod])` spread three membership readers spelled before the 2026-09-23 fold. The two members are the
 * {@link DEPLOY_ENV_TARGETS} labels, which is why the reader lives beside them.
 */
export function perEnvValues<V>(table: Readonly<Record<string, { readonly dev: V; readonly prod: V }>>): V[] {
	return Object.values(table).flatMap((pair) => [pair.dev, pair.prod]);
}

/**
 * The `enum:` set a flow publishes when its graph has NO production arm at all -- a one-member vocabulary, not a
 * narrowing of the two-member one.
 *
 * WHY IT IS A DECLARED CONST RATHER THAN AN INLINE `['dev']`. A flow whose runtime refuses prod and whose PUBLISHED
 * schema still offers it is a contract that advertises a door its own handler slams: the FlowRunner surface, the
 * explorer and every generated input type read the schema, so the offer is what a caller sees. Spelling the dev-only
 * set once means the next such flow adopts the same vocabulary instead of minting an inline literal the enum-drift
 * rules then have to resolve.
 *
 * DEFENCE IN DEPTH, NEVER THE DEFENCE. The runtime refusal (`refuseProdMigrationTarget` over `isDevFleetTarget`) is
 * the control; this only stops a prod target being OFFERED. A schema is validated by the engine at the door, and the
 * door is not the only way into a handler -- a directly-bound executor has no door at all.
 */
export const DEV_ONLY_DEPLOY_ENV_VALUES = [DEPLOY_ENV_TARGETS.DEV] as const;

/** Long-form `WRANGLER_ENV_KEYS` value array for FlowRunner JSON-schema enums. */
export const WRANGLER_ENV_KEY_VALUES = [WRANGLER_ENV_KEYS.DEV, WRANGLER_ENV_KEYS.PRODUCTION] as const;
